import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { VoteGroupService, ElectionService, VoteService } from '../services/firebaseService';
import { useAuth } from '../contexts/AuthContext';
import { VoteGroup, Election, Vote } from '../types';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ArrowLeftIcon,
  UserIcon,
  ShieldCheckIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

interface VoteSummary {
  electionId: string;
  electionTitle: string;
  electionPosition: string;
  candidateId: string | null;
  candidateName: string;
  isAbstention: boolean;
  comment?: string;
}

const VoteSummaryPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { member, isMember } = useAuth();
  
  const [voteGroup, setVoteGroup] = useState<VoteGroup | null>(null);
  const [elections, setElections] = useState<Election[]>([]);
  const [voteSummary, setVoteSummary] = useState<VoteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Récupérer les votes depuis l'état de navigation
  const votes = useMemo(() => location.state?.votes || {}, [location.state?.votes]);
  const comments = useMemo(() => location.state?.comments || {}, [location.state?.comments]);

  useEffect(() => {
    const loadVoteGroup = async () => {
      if (!groupId) return;
      
      // Vérifier si l'utilisateur est connecté en tant que membre
      if (!isMember || !member) {
        setError('Vous devez être connecté en tant que membre pour voter');
        setLoading(false);
        return;
      }
      
      try {
        const [groupData, electionsData] = await Promise.all([
          VoteGroupService.getVoteGroupById(groupId),
          ElectionService.getAllElections()
        ]);

        if (!groupData) {
          setError('Groupe d\'élections introuvable');
          return;
        }

        setVoteGroup(groupData);
        
        // Charger les élections du groupe dans l'ordre
        const groupElections = groupData.elections
          .map(electionId => electionsData.find(e => e.id === electionId))
          .filter(Boolean) as Election[];
        
        setElections(groupElections);

        // Créer le récapitulatif des votes
        const summary: VoteSummary[] = groupElections.map(election => {
          const candidateId = votes[election.id];
          const candidate = election.candidates.find(c => c.id === candidateId);
          
          return {
            electionId: election.id,
            electionTitle: election.title,
            electionPosition: election.position,
            candidateId: candidateId || null,
            candidateName: candidate ? candidate.name : 'Abstention',
            isAbstention: candidateId === null,
            comment: comments[election.id] || ''
          };
        });

        setVoteSummary(summary);

      } catch (error) {
        console.error('Erreur lors du chargement du groupe:', error);
        setError('Erreur lors du chargement des élections');
      } finally {
        setLoading(false);
      }
    };

    loadVoteGroup();
  }, [groupId, votes, comments, member, isMember]);

  const handleModifyVote = (electionId: string) => {
    // Retourner à l'élection spécifique pour modification
    const electionIndex = elections.findIndex(e => e.id === electionId);
    if (electionIndex !== -1) {
      navigate(`/vote/group/${groupId}`, { 
        state: { 
          votes, 
          startFromIndex: electionIndex 
        } 
      });
    }
  };

  const handleSubmitAllVotes = async () => {
    if (!member || !member.email || !groupId) {
      setError('Informations manquantes pour enregistrer les votes. Veuillez vous reconnecter.');
      console.error('Member ou memberEmail manquant:', { member, groupId });
      return;
    }

    // Vérifier que les conditions générales sont acceptées si requises
    if (voteGroup?.requireTermsAccept && !termsAccepted) {
      setError('Vous devez accepter les conditions générales d\'utilisation avant de valider vos votes.');
      setSubmitting(false);
      return;
    }

    // Valider les commentaires obligatoires
    for (const summary of voteSummary) {
      const election = elections.find(e => e.id === summary.electionId);
      if (!election) continue;

      // Trouver le candidat sélectionné
      const selectedCandidate = election.candidates.find(c => c.id === summary.candidateId);
      
      // Vérifier si le commentaire est obligatoire pour ce choix
      if (selectedCandidate && selectedCandidate.commentRequired && !selectedCandidate.allowComments) {
        setError(`Un commentaire obligatoire est requis pour "${selectedCandidate.name}"`);
        setSubmitting(false);
        return;
      }

      // Vérifier si un commentaire obligatoire est requis
      if (selectedCandidate && selectedCandidate.allowComments && selectedCandidate.commentRequired) {
        if (!comments[summary.electionId] || comments[summary.electionId].trim() === '') {
          setError(`Un commentaire obligatoire est requis pour "${selectedCandidate.name}"`);
          setSubmitting(false);
          return;
        }
      }
    }

    setSubmitting(true);
    setError('');

    try {
      // Préparer les votes pour l'enregistrement
      const votesToSave: Array<Omit<Vote, 'id' | 'timestamp'>> = [];
      
      Object.entries(votes).forEach(([electionId, candidateId]) => {
        const isAbstention = candidateId === null;
        const voteData: Omit<Vote, 'id' | 'timestamp'> = {
          memberId: member.email, // Utiliser l'email comme memberId
          electionId,
          groupId,
          candidateId: isAbstention ? undefined : (candidateId as string),
          isAbstention,
          ipAddress: 'browser' // On pourrait récupérer la vraie IP
        };

        // Ajouter le commentaire si présent
        if (comments[electionId]) {
          voteData.comment = comments[electionId];
        }

        votesToSave.push(voteData);
      });

      console.log('Votes à enregistrer:', votesToSave);

      // Enregistrer tous les votes en une seule transaction
      const voteIds = await VoteService.createMultipleVotes(votesToSave);
      
      console.log('Votes enregistrés avec succès:', voteIds);
      
      // Rediriger vers la page de confirmation avec les votes
      navigate('/vote/confirmation', { 
        state: { 
          voteSummary,
          voteGroup,
          voteIds
        } 
      });
      
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement des votes:', error);
      setError('Erreur lors de l\'enregistrement des votes');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
          <p className="text-white text-xl">Chargement du récapitulatif...</p>
        </div>
      </div>
    );
  }

  if (error || !voteGroup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-red-800 to-red-900 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg p-8 max-w-md mx-4">
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{error || 'Données introuvables'}</p>
          {error && error.includes('connecté') && (
            <p className="text-sm text-gray-500 mb-4">
              Veuillez utiliser un lien magique valide pour accéder à cette page.
            </p>
          )}
          <button 
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
      {/* En-tête */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Récapitulatif de vos votes
              </h1>
              <p className="text-blue-100">{voteGroup.name}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center text-white mb-2">
                <ShieldCheckIcon className="w-5 h-5 mr-2" />
                <span className="text-sm">Vérification finale</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Message d'information */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <ShieldCheckIcon className="w-6 h-6 text-yellow-600 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                Vérifiez vos choix avant validation
              </h3>
              <p className="text-yellow-800">
                Veuillez vérifier que vos votes correspondent à vos intentions. 
                Une fois validés, vos votes ne pourront plus être modifiés.
              </p>
            </div>
          </div>
        </div>

        {/* Conditions générales d'utilisation si requises */}
        {voteGroup?.requireTermsAccept && voteGroup?.termsOfUse && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-4">
                Conditions générales d'utilisation
              </h3>
              
              <div className="bg-white rounded-lg p-4 mb-4 max-h-48 overflow-y-auto border border-blue-100">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{voteGroup.termsOfUse}</p>
              </div>
              
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="terms-accept"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1 cursor-pointer"
                />
                <label htmlFor="terms-accept" className="text-sm font-medium text-gray-700 cursor-pointer">
                  J'accepte les conditions générales d'utilisation et je confirme avoir pris connaissance de toutes les conditions
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Récapitulatif des votes */}
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
            <h2 className="text-2xl font-bold text-white">
              Vos choix de votes
            </h2>
            <p className="text-blue-100">
              {voteSummary.length} élection(s) - {voteSummary.filter(v => !v.isAbstention).length} vote(s) exprimé(s)
            </p>
          </div>

          <div className="p-8">
            <div className="space-y-6">
              {voteSummary.map((vote, index) => (
                <div key={vote.electionId} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3">
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {vote.electionTitle}
                          </h3>
                          <p className="text-sm text-gray-600">{vote.electionPosition}</p>
                        </div>
                      </div>

                      {/* Choix du candidat */}
                      <div className="ml-11">
                        {vote.isAbstention ? (
                          <div className="space-y-3">
                            <div className="flex items-center p-4 bg-orange-50 border border-orange-200 rounded-lg">
                              <XCircleIcon className="w-6 h-6 text-orange-500 mr-3" />
                              <div>
                                <p className="font-medium text-orange-900">Abstention</p>
                                <p className="text-sm text-orange-700">Vous vous abstenez pour cette élection</p>
                              </div>
                            </div>
                            {vote.comment && (
                              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <p className="text-xs font-medium text-gray-600 mb-2">Commentaire :</p>
                                <p className="text-sm text-gray-800">{vote.comment}</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg">
                              <CheckCircleIcon className="w-6 h-6 text-green-500 mr-3" />
                              <div className="flex items-center">
                                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mr-4">
                                  <UserIcon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <p className="font-medium text-green-900">{vote.candidateName}</p>
                                  <p className="text-sm text-green-700">Votre choix pour cette élection</p>
                                </div>
                              </div>
                            </div>
                            {vote.comment && (
                              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <p className="text-xs font-medium text-gray-600 mb-2">Commentaire :</p>
                                <p className="text-sm text-gray-800">{vote.comment}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bouton de modification */}
                    <button
                      onClick={() => handleModifyVote(vote.electionId)}
                      className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                    >
                      <PencilIcon className="w-4 h-4 mr-2" />
                      Modifier
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(`/vote/group/${groupId}`, { state: { votes } })}
            className="flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Retour aux élections
          </button>

          <button
            onClick={handleSubmitAllVotes}
            disabled={submitting || (voteGroup?.requireTermsAccept && !termsAccepted)}
            className={`flex items-center px-8 py-3 rounded-lg font-medium transition-all duration-200 ${
              submitting || (voteGroup?.requireTermsAccept && !termsAccepted)
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700 shadow-lg'
            }`}
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Validation en cours...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5 mr-2" />
                Valider tous les votes
              </>
            )}
          </button>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoteSummaryPage;
